import { detectPlatform, isStandalone } from "./platform";

/**
 * Keeps Chrome's install popup for later and remembers whether ANNA is already
 * installed or the person said "Not now". captureInstallPrompt() runs in
 * index.js before React starts, because Chrome can offer the popup right away.
 */

const INSTALLED_KEY = "annaApp.installed";
const SNOOZE_KEY = "annaApp.snoozedUntil";
const DAY = 24 * 60 * 60 * 1000;

let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

const read = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};
const write = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // Storage blocked (private mode): the card may show again, nothing else breaks.
  }
};

export const markInstalled = () => write(INSTALLED_KEY, "1");
export const isMarkedInstalled = () => read(INSTALLED_KEY) === "1";

export const snooze = (days) => write(SNOOZE_KEY, String(Date.now() + days * DAY));
export const isSnoozed = () => Number(read(SNOOZE_KEY) || 0) > Date.now();

// The card only opens by itself when someone comes back on a later day, never on their first day.
const FIRST_DAY_KEY = "annaApp.firstDay";
const localDay = () => {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
};
export const isLaterDay = () => {
  const first = read(FIRST_DAY_KEY);
  return Boolean(first) && first !== localDay();
};

export function captureInstallPrompt() {
  if (typeof window === "undefined") return;
  if (isStandalone()) markInstalled();
  if (!read(FIRST_DAY_KEY)) write(FIRST_DAY_KEY, localDay());

  window.addEventListener("beforeinstallprompt", (event) => {
    // ANNA shows its own card at a calm moment instead of Chrome's bar.
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    markInstalled();
    notify();
  });
}

export const canPromptInstall = () => Boolean(deferredPrompt);

/** Opens Chrome's install popup. Resolves to "accepted", "dismissed" or "unavailable". */
export async function promptInstall() {
  const event = deferredPrompt;
  if (!event) return "unavailable";
  // Each popup event works once; Chrome sends a new one later if this one is dismissed.
  deferredPrompt = null;
  notify();
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === "accepted") markInstalled();
    return outcome;
  } catch (error) {
    return "unavailable";
  }
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Phones and tablets get "Get the app" (account menu, landing page), unless ANNA is already open as the app. */
export const shouldOfferApp = () => detectPlatform().mobile && !isStandalone();

/** The nudges after a test or a booking: phones that haven't installed ANNA yet. */
export const shouldNudgeApp = () => shouldOfferApp() && !isMarkedInstalled();

/** Opens the "Get the ANNA app" card from anywhere (the account menu uses it). */
export const OPEN_EVENT = "anna:get-app";
export const openGetAppCard = () => window.dispatchEvent(new CustomEvent(OPEN_EVENT));
